import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class SignupSettings extends BaseObj {
    public ID: number;
    public __id: number;

    public AutoAssignBibs: boolean;
    public __autoassignbibs: boolean;

    public EventID: number;
    public __eventid: number;

    public OpenFrom: Date;
    public __openfrom: Date;

    public OpenTo: Date;
    public __opento: Date;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AutoAssignBibs?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        OpenFrom?: Date;
        OpenTo?: Date;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<SignupSettings>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('SignupSettings tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AutoAssignBibs = values.AutoAssignBibs ?? false;
        this.__autoassignbibs = values.AutoAssignBibs ?? false;
        Object.defineProperty(this, 'AutoAssignBibs', {
            get(): boolean {
                return this.__autoassignbibs;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('AutoAssignBibs', this.__autoassignbibs, value);
                this.__autoassignbibs = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OpenFrom = values.OpenFrom ?? new Date('1970-01-01T00:00:00.000');
        this.__openfrom = values.OpenFrom ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'OpenFrom', {
            get(): Date {
                return this.__openfrom;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('OpenFrom', this.__openfrom, value);
                this.__openfrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OpenTo = values.OpenTo ?? new Date('1970-01-01T00:00:00.000');
        this.__opento = values.OpenTo ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'OpenTo', {
            get(): Date {
                return this.__opento;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('OpenTo', this.__opento, value);
                this.__opento = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AutoAssignBibs": ${JSON.stringify(this.AutoAssignBibs)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"OpenFrom": ${'"' + formatDateIso(this.OpenFrom) + '"'}` +
            ',' +
            `"OpenTo": ${'"' + formatDateIso(this.OpenTo) + '"'}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of SignupSettings from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AutoAssignBibs?: boolean;
        EventID?: number;
        OpenFrom?: Date;
        OpenTo?: Date;
        EntityState?: 'Added' | 'Unchanged';
    }): SignupSettings {
        return new SignupSettings(values);
    }

    public static _FromServerJson(json: any): SignupSettings {
        return new SignupSettings({
            ID: json.ID,
            AutoAssignBibs: json.AutoAssignBibs,
            EventID: json.EventID,
            OpenFrom: json.OpenFrom instanceof Date ? json.OpenFrom : new Date(json.OpenFrom),
            OpenTo: json.OpenTo instanceof Date ? json.OpenTo : new Date(json.OpenTo),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of SignupSettings from another instance of SignupSettings.
     * @param other The SignupSettings you want to clone
     * @returns A cloned SignupSettings
     */
    public static FromOther(other: SignupSettings): SignupSettings {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return SignupSettings._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return SignupSettings.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'AutoAssignBibs' | 'EventID' | 'OpenFrom' | 'OpenTo')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'SignupSettings';
}
